import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Conversation } from '../types/messages';
import { useAuth } from './useAuth';

export const useConversations = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchConversations = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      console.log('Fetching conversations for user:', user.id);

      const { data, error } = await supabase
        .from('conversation_participants')
        .select(`
          conversation:conversations (
            id,
            created_at,
            participants:conversation_participants (
              user_id,
              unread_count,
              profile:profiles (
                username,
                avatar_url
              )
            ),
            last_message:private_messages (
              content,
              created_at
            )
          )
        `)
        .eq('user_id', user.id)
        .order('conversation(created_at)', { ascending: false });

      if (error) throw error;

      // Récupérer le dernier message pour chaque conversation
      const conversationsWithLastMessage = await Promise.all(
        (data || []).map(async ({ conversation }) => {
          const { data: lastMessage } = await supabase
            .from('private_messages')
            .select('content, created_at')
            .eq('conversation_id', conversation.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          return {
            ...conversation,
            last_message: lastMessage || null,
            last_message_at: lastMessage?.created_at || conversation.created_at
          };
        })
      );

      // Trier les conversations par date du dernier message
      const sortedConversations = conversationsWithLastMessage.sort((a, b) => {
        const dateA = a.last_message?.created_at || a.created_at;
        const dateB = b.last_message?.created_at || b.created_at;
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });

      console.log('Fetched conversations:', sortedConversations);
      setConversations(sortedConversations);
    } catch (err) {
      console.error('Error in fetchConversations:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();

    // Écouter les changements sur les conversations et les participants
    const channel = supabase
      .channel('any_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversations'
        },
        (payload) => {
          console.log('New conversation created:', payload);
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'private_messages'
        },
        (payload) => {
          console.log('New message received:', payload);
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversation_participants',
          filter: `user_id=eq.${user?.id}`
        },
        (payload) => {
          console.log('Participant updated:', payload);
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversation_participants',
          filter: `user_id=eq.${user?.id}`
        },
        (payload) => {
          console.log('New participant added:', payload);
          fetchConversations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'conversations'
        },
        (payload) => {
          console.log('Conversation deleted:', payload);
          fetchConversations();
        }
      )
      .subscribe((status) => {
        console.log('Real-time subscription status:', status);
      });

    return () => {
      console.log('Cleaning up subscription');
      supabase.removeChannel(channel);
    };
  }, [user]);

  return { conversations, loading, error, fetchConversations };
}; 